import { Controller } from 'stimulus'
import $ from 'jquery'

// Message queue (Zed-style): while the assistant is generating a reply you can keep
// composing and "stack up" messages. Queued messages send one at a time, in order, as
// soon as the bot is ready for the next one. Each queued message can be edited, removed,
// or sent right now (which interrupts the current reply).
//
// The queue lives entirely on the client. A queued message is a snapshot of the exact
// fields the composer form would have submitted (model, writing style, Deep Search, …),
// so sending it later reproduces a normal send — POSTed directly to the messages endpoint
// so it never disturbs whatever the user is currently typing in the composer.

// A failed send is retried a few times (transient blips self-heal) before the item is
// parked for the user. Always the HEAD is (re)sent, so order is never broken.
const MAX_SEND_ATTEMPTS = 3
const RETRY_DELAY_MS = 2000

export default class extends Controller {
  static targets = ['list', 'cardTemplate']

  #queue = []
  #editingId = null
  #editingDraft = ''
  #sending = false
  #sendingId = null
  #retryTimer = null
  #nextId = 1

  connect() {
    this.#render()
  }

  disconnect() {
    clearTimeout(this.#retryTimer)
  }

  // ── Called by the composer (chat-bot--message-input) on submit ───────────────────────
  // Returns true when the message was queued (so the composer should block the real send).
  // We queue instead of sending whenever the queue is "active": a reply is streaming, a
  // queued send is in flight, or there are already messages waiting — anything where an
  // immediate send would run out of order.
  maybeEnqueueOnSubmit() {
    const input = this.#input
    if (!input) return false
    if (!this.#isActive) return false
    // v1 queues plain text only — attachments and voice run through their own async
    // pipelines, so a staged file/recording falls through to the normal send.
    if (input.hasAttachmentsOrAudio()) return false

    const html = input.getEditorHtml()
    const text = this.#htmlToText(html)
    if (!text) return false

    this.#enqueue({ html, text })
    input.clearEditor()
    return true
  }

  // The Swift app's composer asks before handing over a message with attachments,
  // which can't be queued: it holds such a message itself while this is true.
  get isActive() {
    return this.#isActive
  }

  get #isActive() {
    return (
      this.#sending ||
      this.#queue.length > 0 ||
      !!this.#input?.isResponseProcessing
    )
  }

  // ── Auto-advance ─────────────────────────────────────────────────────────────────────
  // Called when a reply completes (via the conversation controller) and after local changes.
  // Sends the HEAD of the queue (strictly, never skipping) when the bot is idle and nothing
  // else is in flight — so messages always go out in the order they were queued. A head that
  // has exhausted its retries is parked (errored) and pauses the queue until the user resolves
  // it via send-now / edit / remove; nothing behind it jumps ahead.
  advance() {
    // Bail if the composer was torn down or replaced (e.g. the guest-limit response swaps
    // in the unauth gate) — stops the retry chain from posting into a detached tree.
    if (!this.element.isConnected) return
    if (this.#sending || this.#editingId != null) return
    if (this.#input?.isResponseProcessing) return

    const head = this.#queue[0]
    if (head && !head.error) this.#send(head)
  }

  // ── Card actions ─────────────────────────────────────────────────────────────────────
  edit(event) {
    const item = this.#itemFor(event)
    if (!item) return

    this.#editingId = item.id
    this.#editingDraft = item.text
    this.#render()
  }

  onEditInput(event) {
    this.#editingDraft = event.target.value
  }

  onEditKeydown(event) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.saveEdit(event)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      // Without this, canceling an inline edit also bubbles to any window/document-level Esc
      // handler (e.g. an open modal behind it) and can close that too in the same keypress.
      // ENG-33816.
      event.stopPropagation()
      this.cancelEdit()
    }
  }

  saveEdit(event) {
    const item =
      this.#itemFor(event) || this.#queue.find((i) => i.id === this.#editingId)
    if (!item) return

    const text = this.#editingDraft.trim()
    this.#editingId = null
    this.#editingDraft = ''

    if (!text) {
      // Editing a message down to nothing removes it.
      this.#removeById(item.id)
    } else {
      item.text = text
      item.html = this.#textToHtml(text)
      item.params['chat_bot_message[content]'] = item.html
      item.error = false // an edit is a fix — let it auto-send again
      item.attempts = 0
    }

    this.#render()
    this.advance()
  }

  cancelEdit() {
    this.#editingId = null
    this.#editingDraft = ''
    this.#render()
  }

  remove(event) {
    const item = this.#itemFor(event)
    if (!item) return

    if (this.#editingId === item.id) {
      this.#editingId = null
      this.#editingDraft = ''
    }
    this.#removeById(item.id)
    this.#render()
    this.advance()
  }

  // "Send now" — jump this message ahead of the rest of the queue and send it as soon as the
  // bot is free. If another send is already in flight we can't jump that committed message, so
  // this goes right after it instead.
  sendNow(event) {
    const item = this.#itemFor(event)
    if (!item) return
    if (item.id === this.#sendingId) return // already being sent

    item.error = false // explicit retry clears any prior failure
    item.attempts = 0

    if (this.#sending) {
      // Don't jump the in-flight head — promote this to go next, right after it, so nothing
      // reorders relative to the message that's already committed to the server.
      this.#moveAfterHead(item.id)
      this.#render()
      return
    }

    this.#moveToFront(item.id)
    this.#render()

    if (this.#input?.isResponseProcessing) {
      // Stop the current reply. We never send from here — the send is driven by the reply's
      // completion event, which fires only once it's observably idle (the message controller
      // clears the --processing marker before dispatching it). That event is produced either
      // by the stop's success response (which replaces the reply with a completed message) or,
      // if the stop can't be issued, by the reply finishing on its own. advance() then sends
      // the promoted head — so it's never posted into a running reply, and never left stuck.
      this.#stopCurrentResponse()
    } else {
      this.#send(item)
    }
  }

  // ── Sending ──────────────────────────────────────────────────────────────────────────
  #send(item) {
    clearTimeout(this.#retryTimer) // this send supersedes any pending retry
    this.#sending = true
    this.#sendingId = item.id
    this.#render() // dim the item that's now in flight

    // parent_id must be the CURRENT conversation leaf, not the one captured at enqueue —
    // otherwise a second queued message would branch off the original reply instead of
    // following the one just sent. Read it fresh from the form at send time.
    const parentId = this.#currentParentId()
    const params = parentId
      ? { ...item.params, parent_id: parentId }
      : item.params

    fetch(item.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.#csrfToken,
      },
      body: new URLSearchParams(params).toString(),
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`Queued message failed (${response.status})`)
        return response.json()
      })
      .then((data) => {
        // The response resets/scrolls the composer form and clears its local draft — but
        // this send didn't come from the composer, so drop those so the user's in-progress
        // draft (and its saved copy) are left untouched. reset_form also throws in doUpdate
        // when there's no originating form element.
        delete data.reset_form
        delete data.clear_local_storage
        // The server renders the created messages under `append_if_missing` (`append` is the
        // legacy key); either means the message exists. Same test AjaxHelper uses to render them.
        const created = data.append != null || data.append_if_missing != null
        window.AjaxHelper.doUpdate(data)

        this.#sending = false
        this.#sendingId = null
        if (created) {
          // Queued sends use fetch rather than Rails UJS, so announce the durable save
          // explicitly. The conversations controller refreshes its prefetched empty composer
          // because this message may have changed the sticky model or writing style.
          document.dispatchEvent(new Event('chat-bot:message-sent'))
          // Message created — drop it; the new reply's completion event drives the next send.
          this.#removeById(item.id)
          this.#render()
        } else {
          // No message created — a bare `alert` (in practice only "you've hit the guest limit,
          // enter your email"). The queued text is still the user's, so we keep it visible and
          // flagged rather than dropping it; doUpdate already showed the alert explaining why.
          // The queue pauses here on purpose: a guest at their limit can't send anything more
          // until they sign up, so their remaining queued messages must wait, in order — the
          // user clears this by signing up then retrying, or by removing the card.
          item.error = true
          this.#render()
        }
      })
      .catch((error) => {
        console.error('[Message queue]', error)
        this.#sending = false
        this.#sendingId = null
        item.attempts = (item.attempts || 0) + 1
        if (item.attempts >= MAX_SEND_ATTEMPTS) {
          // Repeated failures — park the head (flagged) for the user; keep it at the front so
          // nothing behind it jumps ahead (FIFO preserved).
          item.error = true
          this.#render()
        } else {
          // Likely transient — retry the SAME head shortly, so order is never broken.
          this.#render()
          this.#retryTimer = setTimeout(() => {
            this.#retryTimer = null
            this.advance()
          }, RETRY_DELAY_MS)
        }
      })
  }

  // Best-effort stop for the in-flight reply. We deliberately don't send from the response
  // callbacks — the promoted head is sent by the reply's completion event (see sendNow), which
  // fires only once the reply is observably idle. The stop just makes that happen sooner:
  // - Stop SUCCEEDS → its response replaces the reply with a completed message → completion event.
  // - Stop FAILS → the reply keeps streaming and finishes on its own → completion event. advance()
  //   here is a no-op while it's processing; it also suppresses the default error toast.
  // - Can't issue a stop (e.g. the message controller is mid-remount) → advance() re-checks: sends
  //   the head if the reply already went idle, otherwise the natural completion event drives it.
  #stopCurrentResponse() {
    const stopUrl = this.#processingMessageController?.stopResponseUrlValue
    if (!stopUrl) return this.advance()
    $.sajax({ url: stopUrl, type: 'POST', error: () => this.advance() })
  }

  get #processingMessageController() {
    const el = this.element
      .closest('.js-chat-bot-conversation')
      ?.querySelector('.chat-bot-message--processing')
    return (
      el &&
      this.application.getControllerForElementAndIdentifier(
        el,
        'chat-bot--message'
      )
    )
  }

  // ── Queue mutation ───────────────────────────────────────────────────────────────────
  #enqueue({ html, text }) {
    this.#queue.push({
      id: this.#nextId++,
      html,
      text,
      url: this.#composerForm?.getAttribute('action'),
      params: this.#snapshotParams(html),
      error: false,
      attempts: 0,
    })
    this.#render()
    // Resume immediately if the bot is already idle (e.g. the queue had stalled).
    this.advance()
  }

  #removeById(id) {
    this.#queue = this.#queue.filter((item) => item.id !== id)
  }

  #moveToFront(id) {
    const index = this.#queue.findIndex((item) => item.id === id)
    if (index > 0) this.#queue.unshift(this.#queue.splice(index, 1)[0])
  }

  // Move to just after the head — used by send-now while a send is in flight, so the promoted
  // item goes next without jumping the already-committed in-flight message at index 0.
  #moveAfterHead(id) {
    const index = this.#queue.findIndex((item) => item.id === id)
    if (index > 1) this.#queue.splice(1, 0, this.#queue.splice(index, 1)[0])
  }

  // Capture exactly what the composer form would submit right now (model, writing style,
  // Deep Search, CSRF token, hidden fields …). serializeArray() skips file inputs, so this
  // is inherently the text-only payload. Content is overridden with the live editor HTML;
  // parent_id is dropped here and re-read fresh at send time (see #send).
  #snapshotParams(html) {
    const params = {}
    $(this.#composerForm)
      .serializeArray()
      .forEach(({ name, value }) => {
        params[name] = value
      })
    params['chat_bot_message[content]'] = html
    delete params['parent_id']
    return params
  }

  // The conversation leaf: each message contributes a parent_id field bound to the composer
  // form (via HTML form=), in document order, so the last one is the latest message.
  #currentParentId() {
    const ids = $(this.#composerForm)
      .serializeArray()
      .filter((field) => field.name === 'parent_id')
    return ids.length ? ids[ids.length - 1].value : null
  }

  // ── Rendering ────────────────────────────────────────────────────────────────────────
  #render() {
    // Bail if the composer was torn down (e.g. navigating away mid-send).
    if (!this.hasListTarget || !this.hasCardTemplateTarget) return

    this.listTarget.replaceChildren()

    this.#queue.forEach((item) => {
      const card =
        this.cardTemplateTarget.content.firstElementChild.cloneNode(true)
      card.dataset.queueId = item.id
      // Dim the item whose send is in flight; flag a failed send with a red border.
      card.classList.toggle('opacity-60', item.id === this.#sendingId)
      card.classList.toggle('border-red-500', item.error)

      const editing = item.id === this.#editingId
      card
        .querySelector('.js-queued-message-display')
        .classList.toggle('hidden', editing)
      card
        .querySelector('.js-queued-message-edit')
        .classList.toggle('hidden', !editing)

      if (editing) {
        card.querySelector('.js-queued-message-textarea').value =
          this.#editingDraft
      } else {
        card.querySelector('.js-queued-message-text').textContent = item.text
      }

      this.listTarget.appendChild(card)
    })

    this.element.classList.toggle('hidden', this.#queue.length === 0)

    // The cards are cloned from a <template> and inserted directly, so the global
    // behaviors (tooltips on the icon buttons, etc.) haven't been wired up — .changed()
    // activates them, the same way AjaxHelper does for content it inserts.
    if (this.#queue.length) $(this.listTarget).changed()

    if (this.#editingId != null) this.#focusEditor()
  }

  #focusEditor() {
    requestAnimationFrame(() => {
      const textarea = this.listTarget.querySelector(
        `.js-queued-message[data-queue-id="${
          this.#editingId
        }"] .js-queued-message-textarea`
      )
      if (!textarea) return
      textarea.focus()
      textarea.setSelectionRange(textarea.value.length, textarea.value.length)
    })
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────────────
  #itemFor(event) {
    const id = Number(
      event.target.closest('.js-queued-message')?.dataset.queueId
    )
    return this.#queue.find((item) => item.id === id)
  }

  get #input() {
    const el = this.#composerRoot
    return (
      el &&
      this.application.getControllerForElementAndIdentifier(
        el,
        'chat-bot--message-input'
      )
    )
  }

  get #composerRoot() {
    return this.element.closest('.chat-bot-message-input-component')
  }

  get #composerForm() {
    return this.element.closest('form')
  }

  get #csrfToken() {
    return document.querySelector('[name="csrf-token"]')?.content ?? ''
  }

  #htmlToText(html) {
    const el = document.createElement('div')
    el.innerHTML = html
    return el.textContent.trim()
  }

  #textToHtml(text) {
    return `<p>${this.#escapeHtml(text).replace(/\n/g, '<br>')}</p>`
  }

  #escapeHtml(str) {
    const el = document.createElement('span')
    el.textContent = str
    return el.innerHTML
  }
}
